import { Injectable } from '@angular/core';
import { Probate } from '../Models/Probate';
import { Bequest } from '../Models/Bequest';
import { Executor } from '../Models/Executor';
import { ProbateAddress } from '../Models/ProbateAddress';
import { Precondition } from '../Models/Precondition';
import { Solicitor } from '../Models/Solicitor';

export interface FeedbackCandidate {
  fieldPath: string;
  beforeValue: string | null;
  afterValue: string | null;
  suggestedFeedbackTypeId?: number;
  // "<Beneficiary Name on Will> - <Associated Text on Will>" - identifies WHICH bequest a
  // bequest-level row is about, since a per-field row (e.g. "Bequest - Type Changed") no longer
  // shows the beneficiary name the way the old whole-summary rows always did. Only ever set for
  // bequest changes (see buildBequestIdentifier) - never set for probate/address/executor/
  // solicitor/precondition rows, which don't need it.
  bequestIdentifier?: string;
}

// FeedbackType ids, per the seeded FeedbackType lookup table. 12/13 require
// database/scripts/feedback_schema_changes.sql to have been run first.
const FEEDBACK_TYPE = {
  MissingPrecondition: 1,
  IncorrectPrecondition: 2,
  MissingWishes: 3,
  IncorrectWishes: 4,
  IncorrectBequestDetails: 5,
  MissingBequest: 6,
  RevokedGift: 7,
  IncorrectExecutorDetails: 8,
  IncorrectBeneficiaryLink: 9,
  IncorrectAssociatedText: 10,
  IncorrectDeceasedDetails: 11,
  IncorrectBequestType: 12
};

interface ScalarField {
  key: keyof Probate;
  label: string;
  isDate?: boolean;
  suggestedFeedbackTypeId?: number;
}

const SCALAR_FIELDS: ScalarField[] = [
  { key: 'deceasedName', label: 'Deceased Name', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'title', label: 'Title', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'afterTitle', label: 'After Title', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'gender', label: 'Gender', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'dateOfDeath', label: 'Date of Death', isDate: true, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'dateOfBirth', label: 'Date of Birth', isDate: true, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'relationshipToRelative', label: 'Relationship to Relative', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'registryName', label: 'Registry' },
  { key: 'dateOfIssue', label: 'Date of Issue', isDate: true },
  { key: 'grossValue', label: 'Gross Value', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'netValue', label: 'Net Value', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'notes', label: 'Footer' },
  { key: 'benhamClause', label: 'Benham Clause' }
];

@Injectable({ providedIn: 'root' })
export class FeedbackTrackingService {
  private snapshot: Probate | null = null;

  get isTracking(): boolean {
    return this.snapshot !== null;
  }

  hasSnapshot(): boolean {
    return this.snapshot !== null;
  }

  startTracking(probate: Probate): void {
    this.snapshot = JSON.parse(JSON.stringify(probate));
  }

  stopTracking(): void {
    this.snapshot = null;
  }

  computeCandidates(current: Probate, formatBequestType: (bequestType: any) => string): FeedbackCandidate[] {
    if (!this.snapshot) return [];
    const before = this.snapshot;
    const candidates: FeedbackCandidate[] = [];

    for (const field of SCALAR_FIELDS) {
      const beforeStr = this.toComparableString((before as any)[field.key], field.isDate);
      const afterStr = this.toComparableString((current as any)[field.key], field.isDate);
      if (beforeStr !== afterStr) {
        candidates.push({
          fieldPath: field.label,
          beforeValue: beforeStr,
          afterValue: afterStr,
          suggestedFeedbackTypeId: field.suggestedFeedbackTypeId
        });
      }
    }

    this.diffAliases(before.aliases, current.aliases, candidates);
    this.diffCodicilDates(before.CodicilDates, current.CodicilDates, candidates);
    this.diffAddresses(before.addresses, current.addresses, candidates);
    this.diffExecutors(before.executors, current.executors, candidates);
    this.diffSolicitor(before.solicitor, current.solicitor, candidates);
    this.diffPreconditions(before.preconditions, current.preconditions, candidates);
    this.diffBequests(before.bequests, current.bequests, candidates, formatBequestType);

    return candidates;
  }

  private diffAliases(before: string[] | undefined, after: string[] | undefined, candidates: FeedbackCandidate[]): void {
    const beforeSet = new Set((before || []).map(a => (a || '').trim()).filter(a => a.length > 0));
    const afterSet = new Set((after || []).map(a => (a || '').trim()).filter(a => a.length > 0));

    for (const alias of afterSet) {
      if (!beforeSet.has(alias)) {
        candidates.push({ fieldPath: 'Alias Added', beforeValue: null, afterValue: alias });
      }
    }
    for (const alias of beforeSet) {
      if (!afterSet.has(alias)) {
        candidates.push({ fieldPath: 'Alias Removed', beforeValue: alias, afterValue: null });
      }
    }
  }

  // Was entirely untracked before - adding/removing a codicil date never showed up as feedback
  // at all, because nothing in this service ever looked at probate.CodicilDates. Matched by
  // formatted date string (not id - codicil dates have none), same shape as diffAliases.
  private diffCodicilDates(before: Date[] | undefined, after: Date[] | undefined, candidates: FeedbackCandidate[]): void {
    const beforeSet = new Set((before || []).map(d => this.toComparableString(d, true)).filter((d): d is string => d !== null));
    const afterSet = new Set((after || []).map(d => this.toComparableString(d, true)).filter((d): d is string => d !== null));

    for (const date of afterSet) {
      if (!beforeSet.has(date)) {
        candidates.push({ fieldPath: 'Codicil Date Added', beforeValue: null, afterValue: date });
      }
    }
    for (const date of beforeSet) {
      if (!afterSet.has(date)) {
        candidates.push({ fieldPath: 'Codicil Date Removed', beforeValue: date, afterValue: null });
      }
    }
  }

  private static readonly ADDRESS_FIELDS: { key: keyof ProbateAddress; label: string }[] = [
    { key: 'address', label: 'Address Line' },
    { key: 'town', label: 'Town' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'country', label: 'Country' }
  ];

  private diffAddresses(before: ProbateAddress[] | undefined, after: ProbateAddress[] | undefined, candidates: FeedbackCandidate[]): void {
    const key = (a: ProbateAddress) => a.id > 0 ? `id:${a.id}` : `key:${(a.address || '').trim().toLowerCase()}|${(a.postcode || '').trim().toLowerCase()}`;
    const summary = (a: ProbateAddress) => [a.address, a.town, a.county, a.postcode, a.country].filter(p => p && p.trim().length > 0).join(', ');

    const { added, removed, matched } = this.matchByKey(before || [], after || [], key);

    // Every entry here is one of the deceased's own addresses (current or former - see
    // ProbateAddress.isFinal), never a third party's, so any change is a deceased-detail correction.
    // A brand-new or fully-removed address has no per-field "before"/"after" to diff against, so
    // those two still show the whole address - only a genuine edit to an existing address (matched)
    // is broken down field by field, so an unrelated field being unchanged never shows up.
    for (const a of added) candidates.push({ fieldPath: 'Address Added', beforeValue: null, afterValue: summary(a), suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails });
    for (const a of removed) candidates.push({ fieldPath: 'Address Removed', beforeValue: summary(a), afterValue: null, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails });
    for (const { before: b, after: a } of matched) {
      for (const field of FeedbackTrackingService.ADDRESS_FIELDS) {
        const beforeStr = ((b[field.key] as string) || '').trim() || null;
        const afterStr = ((a[field.key] as string) || '').trim() || null;
        if (beforeStr !== afterStr) {
          candidates.push({
            fieldPath: `Address - ${field.label} Changed`,
            beforeValue: beforeStr,
            afterValue: afterStr,
            suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails
          });
        }
      }
    }
  }

  private static readonly SOLICITOR_FIELDS: { key: keyof Solicitor; label: string }[] = [
    { key: 'name', label: 'Name' },
    { key: 'reference', label: 'Reference' },
    { key: 'address', label: 'Address' },
    { key: 'town', label: 'Town' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'country', label: 'Country' }
  ];

  // Was entirely untracked before - solicitor ("Extracted By") edits never showed up as feedback
  // at all, because nothing in this service ever looked at probate.solicitor.
  private diffSolicitor(before: Solicitor | undefined, after: Solicitor | undefined, candidates: FeedbackCandidate[]): void {
    if (!before && !after) return;
    for (const field of FeedbackTrackingService.SOLICITOR_FIELDS) {
      const beforeStr = (((before as any)?.[field.key] as string) || '').trim() || null;
      const afterStr = (((after as any)?.[field.key] as string) || '').trim() || null;
      if (beforeStr !== afterStr) {
        candidates.push({
          fieldPath: `Solicitor - ${field.label} Changed`,
          beforeValue: beforeStr,
          afterValue: afterStr
        });
      }
    }
  }

  private diffExecutors(before: Executor[] | undefined, after: Executor[] | undefined, candidates: FeedbackCandidate[]): void {
    const key = (e: Executor) => e.id > 0 ? `id:${e.id}` : `name:${(e.name || '').trim().toLowerCase()}`;
    const summary = (e: Executor) => [e.name, e.relationship, e.address, e.town, e.county, e.postcode, e.country].filter(p => p && p.trim().length > 0).join(', ');

    const { added, removed, matched } = this.matchByKey(before || [], after || [], key);

    for (const e of added) candidates.push({ fieldPath: 'Executor Added', beforeValue: null, afterValue: summary(e), suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectExecutorDetails });
    for (const e of removed) candidates.push({ fieldPath: 'Executor Removed', beforeValue: summary(e), afterValue: null, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectExecutorDetails });
    for (const { before: b, after: a } of matched) {
      const beforeSummary = summary(b);
      const afterSummary = summary(a);
      if (beforeSummary !== afterSummary) {
        candidates.push({ fieldPath: 'Executor Changed', beforeValue: beforeSummary, afterValue: afterSummary, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectExecutorDetails });
      }
    }
  }

  // Matches preconditions by id (falling back to text only for a brand-new one that has no id
  // yet), not by text-as-a-set - a plain edit to existing wording is then a single "Precondition
  // Changed" row instead of a Removed+Added pair for two unrelated-looking strings. This same
  // identity is what classifyBequestChange/diffBequestFields key off for a bequest's precondition
  // link, so editing a precondition's own wording no longer falsely flags every bequest under it.
  private diffPreconditions(before: Precondition[] | undefined, after: Precondition[] | undefined, candidates: FeedbackCandidate[]): void {
    const key = (p: Precondition) => this.preconditionKey(p)!;
    const beforeList = (before || []).filter(p => (p.text || '').trim().length > 0);
    const afterList = (after || []).filter(p => (p.text || '').trim().length > 0);

    const { added, removed, matched } = this.matchByKey(beforeList, afterList, key);

    for (const p of added) candidates.push({ fieldPath: 'Precondition Added', beforeValue: null, afterValue: p.text, suggestedFeedbackTypeId: FEEDBACK_TYPE.MissingPrecondition });
    for (const p of removed) candidates.push({ fieldPath: 'Precondition Removed', beforeValue: p.text, afterValue: null, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectPrecondition });
    for (const { before: b, after: a } of matched) {
      if ((b.text || '').trim() !== (a.text || '').trim()) {
        candidates.push({ fieldPath: 'Precondition Changed', beforeValue: b.text, afterValue: a.text, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectPrecondition });
      }
    }
  }

  // Shared identity key for a precondition - by id once it has one (editing its text never
  // changes its id), by text only while it's still unsaved (id undefined) this session.
  private preconditionKey(p: Precondition | null | undefined): string | null {
    if (!p) return null;
    return p.id ? `id:${p.id}` : `text:${(p.text || '').trim().toLowerCase()}`;
  }

  private diffBequests(before: Bequest[] | undefined, after: Bequest[] | undefined, candidates: FeedbackCandidate[], formatBequestType: (bequestType: any) => string): void {
    const beforeList = before || [];
    const afterList = after || [];

    const beforeById = new Map(beforeList.filter(b => b.id > 0).map(b => [b.id, b]));
    const afterById = new Map(afterList.filter(b => b.id > 0).map(b => [b.id, b]));

    for (const bequest of afterList) {
      if (bequest.id <= 0) {
        candidates.push({
          fieldPath: 'Bequest Added',
          beforeValue: null,
          afterValue: this.buildBequestSummary(bequest, formatBequestType),
          suggestedFeedbackTypeId: FEEDBACK_TYPE.MissingBequest,
          // No "before" state exists yet for a brand-new bequest, so identify it from what it
          // was actually added as.
          bequestIdentifier: this.buildBequestIdentifier(bequest)
        });
      }
    }

    for (const [id, beforeBequest] of beforeById) {
      const afterBequest = afterById.get(id);
      if (!afterBequest) {
        candidates.push({
          fieldPath: 'Bequest Removed',
          beforeValue: this.buildBequestSummary(beforeBequest, formatBequestType),
          afterValue: null,
          suggestedFeedbackTypeId: FEEDBACK_TYPE.RevokedGift,
          bequestIdentifier: this.buildBequestIdentifier(beforeBequest)
        });
        continue;
      }

      // One candidate PER field that actually changed, not one row dumping the whole bequest -
      // an edit to just the beneficiary link (say) no longer also shows the untouched bequest
      // type/value alongside it.
      candidates.push(...this.diffBequestFields(beforeBequest, afterBequest, formatBequestType));
    }
  }

  // Returns zero or more field-level changes between the two bequests - empty if nothing
  // changed at all. Each entry carries its own before/after (just that field, not the whole
  // bequest) and its own suggested FeedbackType, so several aspects changing at once produce
  // several distinct, individually-categorisable rows instead of one blended one.
  private diffBequestFields(before: Bequest, after: Bequest, formatBequestType: (bequestType: any) => string): FeedbackCandidate[] {
    const norm = (v: any) => (v === undefined || v === null || v === '') ? null : v;
    const numEqual = (a: any, b: any) => {
      const an = norm(a);
      const bn = norm(b);
      if (an === null || bn === null) return an === bn;
      return Number(an) === Number(bn);
    };
    const categoriesEqual = (a: number[] | null | undefined, b: number[] | null | undefined) => {
      const aSorted = [...(a || [])].sort((x, y) => x - y).join(',');
      const bSorted = [...(b || [])].sort((x, y) => x - y).join(',');
      return aSorted === bSorted;
    };
    const bequestName = (b: Bequest) => b.organisationName || b.beneficiaryNameOnWill || b.name || 'Unnamed Beneficiary';
    const bequestValue = (b: Bequest): string | null => {
      if (b.cashAmount !== null && b.cashAmount !== undefined) return `£${Number(b.cashAmount).toLocaleString('en-GB')}`;
      if (b.percentageAmount !== null && b.percentageAmount !== undefined) return `${b.percentageAmount}%`;
      if (b.effectDescription) return b.effectDescription;
      return null;
    };

    // The identifier for every row about this bequest, whichever field(s) actually changed - see
    // buildBequestIdentifier. Taken from `before`, not `after`: it's meant to be the constant the
    // bequest was already known by prior to this edit, so it stays stable and recognisable even
    // for the "Bequest - Name on Will Changed" row itself.
    const identifier = this.buildBequestIdentifier(before);

    const changes: FeedbackCandidate[] = [];

    if (norm(before.beneficiaryId) !== norm(after.beneficiaryId)) {
      changes.push({
        fieldPath: 'Bequest - Beneficiary Changed',
        beforeValue: bequestName(before),
        afterValue: bequestName(after),
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectBeneficiaryLink,
        bequestIdentifier: identifier
      });
    }

    const wishesBefore = norm(before.wishes);
    const wishesAfter = norm(after.wishes);
    if (wishesBefore !== wishesAfter) {
      changes.push({
        fieldPath: 'Bequest - Wishes Changed',
        beforeValue: wishesBefore,
        afterValue: wishesAfter,
        suggestedFeedbackTypeId: wishesBefore === null ? FEEDBACK_TYPE.MissingWishes : FEEDBACK_TYPE.IncorrectWishes,
        bequestIdentifier: identifier
      });
    }

    const assocBefore = norm(before.associatedTextOnWill);
    const assocAfter = norm(after.associatedTextOnWill);
    if (assocBefore !== assocAfter) {
      changes.push({
        fieldPath: 'Bequest - Associated Text Changed',
        beforeValue: assocBefore,
        afterValue: assocAfter,
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectAssociatedText,
        bequestIdentifier: identifier
      });
    }

    if (norm(before.bequestType) !== norm(after.bequestType)) {
      changes.push({
        fieldPath: 'Bequest - Type Changed',
        beforeValue: formatBequestType(before.bequestType) || null,
        afterValue: formatBequestType(after.bequestType) || null,
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectBequestType,
        bequestIdentifier: identifier
      });
    }

    const nameBefore = norm(before.beneficiaryNameOnWill);
    const nameAfter = norm(after.beneficiaryNameOnWill);
    if (nameBefore !== nameAfter) {
      changes.push({
        fieldPath: 'Bequest - Name on Will Changed',
        beforeValue: nameBefore,
        afterValue: nameAfter,
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectBequestDetails,
        bequestIdentifier: identifier
      });
    }

    if (!numEqual(before.cashAmount, after.cashAmount)
      || !numEqual(before.percentageAmount, after.percentageAmount)
      || norm(before.effectDescription) !== norm(after.effectDescription)) {
      changes.push({
        fieldPath: 'Bequest - Value Changed',
        beforeValue: bequestValue(before),
        afterValue: bequestValue(after),
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectBequestDetails,
        bequestIdentifier: identifier
      });
    }

    if (!categoriesEqual(before.charityCategoryId, after.charityCategoryId)) {
      changes.push({
        fieldPath: 'Bequest - Category Changed',
        beforeValue: (before.charityCategoryId || []).join(', ') || null,
        afterValue: (after.charityCategoryId || []).join(', ') || null,
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectBequestDetails,
        bequestIdentifier: identifier
      });
    }

    // By identity (see preconditionKey), not text - so re-wording the linked precondition
    // itself, without changing which precondition this bequest is actually under, is not
    // reported here at all (diffPreconditions already reports that edit once, on its own).
    if (this.preconditionKey(before.precondition) !== this.preconditionKey(after.precondition)) {
      changes.push({
        fieldPath: 'Bequest - Precondition Link Changed',
        beforeValue: before.precondition?.text ?? null,
        afterValue: after.precondition?.text ?? null,
        suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectBequestDetails,
        bequestIdentifier: identifier
      });
    }

    return changes;
  }

  // "<Beneficiary Name on Will> - <Associated Text on Will>" (or just the name, if there's no
  // associated text) - a stable-ish identifier for which bequest a row is about, since Beneficiary
  // Name on Will is set once when the bequest is first loaded in and Associated Text on Will
  // disambiguates the (rarer) case of several bequests of different types to the same beneficiary.
  // Not unique in every case, but good enough to tell the user which bequest a supervisor's
  // amendment applies to - shown as a hover tooltip rather than inline since the associated text
  // can run long.
  private buildBequestIdentifier(bequest: Bequest): string | undefined {
    const name = (bequest.beneficiaryNameOnWill || '').trim();
    if (!name) return undefined;
    const associatedText = (bequest.associatedTextOnWill || '').trim();
    return associatedText ? `${name} - ${associatedText}` : name;
  }

  private buildBequestSummary(bequest: Bequest, formatBequestType: (bequestType: any) => string): string {
    const name = bequest.organisationName || bequest.beneficiaryNameOnWill || bequest.name || 'Unnamed Beneficiary';
    const notification = (bequest.notificationType || '').toUpperCase() || 'UNKNOWN';
    const type = formatBequestType(bequest.bequestType) || '—';
    let value: string;
    if (bequest.cashAmount !== null && bequest.cashAmount !== undefined) {
      value = `£${Number(bequest.cashAmount).toLocaleString('en-GB')}`;
    } else if (bequest.percentageAmount !== null && bequest.percentageAmount !== undefined) {
      value = `${bequest.percentageAmount}%`;
    } else if (bequest.effectDescription) {
      value = bequest.effectDescription;
    } else {
      value = '—';
    }
    return `${name} - ${notification} - ${type} - ${value}`;
  }

  private matchByKey<T>(before: T[], after: T[], key: (item: T) => string): { added: T[]; removed: T[]; matched: { before: T; after: T }[] } {
    const beforeMap = new Map(before.map(item => [key(item), item]));
    const afterMap = new Map(after.map(item => [key(item), item]));

    const added: T[] = [];
    const removed: T[] = [];
    const matched: { before: T; after: T }[] = [];

    for (const [k, item] of afterMap) {
      const beforeItem = beforeMap.get(k);
      if (beforeItem) {
        matched.push({ before: beforeItem, after: item });
      } else {
        added.push(item);
      }
    }
    for (const [k, item] of beforeMap) {
      if (!afterMap.has(k)) {
        removed.push(item);
      }
    }

    return { added, removed, matched };
  }

  // Normalizes a scalar value (Date / Moment-like / number / string) to a stable string for both
  // comparison and display, so JSON round-tripping the snapshot (Date -> ISO string) or differing
  // Date/Moment representations between the snapshot and the live form don't produce phantom diffs.
  private toComparableString(value: any, isDate?: boolean): string | null {
    if (value === null || value === undefined || value === '') return null;

    if (isDate) {
      let d: Date | null = null;
      if (typeof value === 'object' && typeof value.format === 'function') {
        // Moment-like object
        return value.format('DD/MM/YYYY');
      } else if (value instanceof Date) {
        d = value;
      } else {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) d = parsed;
      }
      if (!d) return String(value);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}/${d.getFullYear()}`;
    }

    if (typeof value === 'number') return String(value);

    return String(value).trim() || null;
  }
}
